import { useState } from "react";
import { Button, Stack, Typography, Box, Checkbox, FormControlLabel, List, ListItem, ListItemText } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Delete } from "@mui/icons-material";
import { useWorkspace } from "hooks/queries/useWorkspace";
import { useParams } from "react-router-dom";
import { useBatch } from "hooks/queries/useBatch";
import { Document } from "types";

export type DocumentDeleteProps = {
    documents: Document[];
    onSuccess?: () => void;
    onCancel?: () => void;
};

export function DocumentDelete({ documents, onSuccess, onCancel }: DocumentDeleteProps) {
    const { workspaceId } = useParams<{ workspaceId: string }>();
    const workspace = useWorkspace(workspaceId!);
    const connectorConfig = workspace.data?.config.connectorConfig;
    const batchMutation = useBatch(workspace.data?.config);
    const [confirmChecked, setConfirmChecked] = useState(false);

    const handleDelete = async () => {
        const requests = documents.map(d => ({
            method: "delete",
            url: `${connectorConfig.entity}/${d.id}`,
            body: {}
        }));
        await batchMutation.mutateAsync(requests);
        onSuccess?.();
    };

    return (
        <Stack gap={2}>
            <Typography variant="subtitle1">You are about to delete {documents.length} documents.</Typography>
            <Typography variant="subtitle1">You can restore deleted documents by contacting BEYON support.</Typography>
            <Box display="flex" alignItems="center" width="100%">
                <Box flex={1} display="flex" justifyContent="flex-start">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={confirmChecked}
                                onChange={e => setConfirmChecked(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Yes, I am sure, delete these documents."
                    />
                </Box>
                <Box flex={1} display="flex" justifyContent="flex-end" gap={2}>
                    <Button variant="outlined" color="primary" onClick={onCancel}>Cancel</Button>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        loading={batchMutation.isPending}
                        loadingPosition="end"
                        endIcon={<Delete />}
                        onClick={handleDelete}
                        disabled={!confirmChecked}
                    >
                        Delete
                    </LoadingButton>
                </Box>
            </Box>
        </Stack>
    );
}
